import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { IdCard, Users, ArrowRight, ExternalLink, Plus, CalendarDays } from "lucide-react";

const STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  inactive: "bg-stone-100 text-stone-700 border-stone-200",
  suspended: "bg-rose-100 text-rose-800 border-rose-200",
  unavailable: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function Drivers() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [items, setItems] = useState([]);
  const [externalItems, setExternalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExternal, setShowExternal] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/drivers").catch((e) => { toast.error(formatApiError(e)); return { data: [] }; }),
      api.get("/external-drivers").catch(() => ({ data: [] })),
    ]).then(([drivers, ext]) => {
      setItems(drivers.data);
      setExternalItems(ext.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Driver Administration</h1>
          <p className="text-slate-600 mt-1">Manage driver status, availability, documents, and assignments.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/app/driver-service" data-testid="drivers-service-link"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 font-medium text-slate-700 hover:bg-slate-50">
            Readiness &amp; dispatch
          </Link>
          <Link to="/app/drivers/calendar" data-testid="drivers-calendar-link"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 font-medium text-slate-700 hover:bg-slate-50">
            <CalendarDays className="w-4 h-4" /> Driver Calendar
          </Link>
          {isOwner && (
            <Link to="/app/employees" data-testid="drivers-add-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium">
              <Plus className="w-4 h-4" /> Add driver via Employees
            </Link>
          )}
        </div>
      </div>

      {loading && <div className="text-slate-500">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="drivers-empty">
          <IdCard className="w-8 h-8 mx-auto text-slate-400" />
          <p className="mt-2 text-slate-600">
            No drivers yet. Mark a staff member as a driver from the{" "}
            <Link to="/app/employees" className="underline">Employees</Link> page.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <Link key={d.id} to={`/app/drivers/${d.id}`}
                className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#2D6A4F] transition-colors"
                data-testid={`driver-card-${d.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold truncate">
                  {d.driver_display_name || d.name}
                </h3>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border shrink-0 ${STATUS_TONE[d.driver_status] || STATUS_TONE.active}`}
                    data-testid={`driver-status-${d.id}`}>
                {d.driver_status || "active"}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
              {d.license_expiry && <span>License expires {String(d.license_expiry).slice(0, 10)}</span>}
              <span>{d.upcoming_assignments || 0} upcoming assignment{d.upcoming_assignments === 1 ? "" : "s"}</span>
            </div>
            <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#2D6A4F]">
              View profile <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        ))}
      </div>

      {externalItems.length > 0 && (
        <div>
          <button onClick={() => setShowExternal((s) => !s)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"
                  data-testid="drivers-toggle-external">
            <ExternalLink className="w-4 h-4" /> External drivers ({externalItems.length})
          </button>
          {showExternal && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {externalItems.map((ext) => (
                <div key={ext.id} className="rounded-xl border border-dashed border-slate-300 bg-white p-5"
                     data-testid={`external-driver-card-${ext.id}`}>
                  <h3 className="font-display text-base font-semibold">{ext.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{ext.phone || "No phone on file"}</p>
                  {ext.note && <p className="text-xs text-slate-500 mt-1">{ext.note}</p>}
                  <span className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full font-medium border ${
                    ext.status === "inactive" ? STATUS_TONE.inactive : STATUS_TONE.active
                  }`}>
                    {ext.status || "active"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
